import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  Container,
  IconButton,
  InputAdornment,
  Alert,
  useTheme,
  useMediaQuery,
  Paper,
  Chip,
} from '@mui/material';
import { Google, Visibility, VisibilityOff, AutoGraph, Psychology, School, Assignment, Timer, Analytics } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { auth } from '../../firebase';
import { fetchSignInMethodsForEmail, signInWithEmailAndPassword } from 'firebase/auth';

const LandingPage = () => {
  const navigate = useNavigate();
  const { signInWithGoogle } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [authError, setAuthError] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const learnMoreRef = useRef(null);

  const handleGoogleSignIn = async () => {
    try {
      await signInWithGoogle();
      navigate('/app');
    } catch (error) {
      setAuthError('Failed to sign in with Google. Please try again.');
    }
  };

  const handleEmailContinue = async () => {
    if (!email) {
      setEmailError('Please enter an email address');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setEmailError('');
    setAuthError('');

    try {
      const methods = await fetchSignInMethodsForEmail(auth, email);
      const userExists = methods.length > 0;
      
      if (userExists) {
        setIsNewUser(false);
        setShowPasswordInput(true);
      } else {
        setIsNewUser(true);
        setShowPasswordInput(true);
      }
    } catch (error) {
      // Handle specific Firebase errors
      if (error.code === 'auth/invalid-email') {
        setEmailError('Invalid email format');
      } else {
        console.error('Email check error:', error);
        setAuthError('Error checking email. Please try again.');
      }
    }
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setAuthError('Please enter a password');
      return;
    }

    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate('/app');
    } catch (error) {
      if (error.code === 'auth/wrong-password') {
        setAuthError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/too-many-requests') {
        setAuthError('Too many failed attempts. Please try again later.');
      } else {
        setAuthError('Failed to sign in. Please try again.');
      }
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const scrollToLearnMore = () => {
    learnMoreRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: 'column',
      minHeight: '100vh',
      bgcolor: '#fff',
      color: '#000',
      overflow: 'hidden',
    }}>
      {/* Main Section with Left and Right Sides */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: '100vh',
        gap: { xs: 4, md: 0 },
      }}>
        {/* Left Side - Enhanced Message */}
        <Box 
          component={motion.div}
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          sx={{ 
            flex: { xs: 'none', md: 1 },
            minHeight: { xs: 'auto', md: '100vh' },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            p: { xs: 2, sm: 3, md: 6 },
            position: 'relative',
            background: 'linear-gradient(135deg, #f6f8ff 0%, #ffffff 100%)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '10%',
              left: '5%',
              width: { xs: '200px', md: '300px' },
              height: { xs: '200px', md: '300px' },
              background: 'radial-gradient(circle, rgba(79, 70, 229, 0.1) 0%, rgba(79, 70, 229, 0) 70%)',
              borderRadius: '50%',
              zIndex: 0,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '15%',
              right: '10%',
              width: { xs: '150px', md: '200px' },
              height: { xs: '150px', md: '200px' },
              background: 'radial-gradient(circle, rgba(182, 95, 59, 0.1) 0%, rgba(182, 95, 59, 0) 70%)',
              borderRadius: '50%',
              zIndex: 0,
            }
          }}
        >
          <Container maxWidth="md" sx={{ 
            position: 'relative', 
            zIndex: 1,
            px: { xs: 2, sm: 3, md: 4 }
          }}>
            <Box
              component={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <Typography 
                variant="overline" 
                sx={{ 
                  color: '#4F46E5',
                  fontWeight: 600,
                  letterSpacing: 2,
                  mb: 2,
                  display: 'block',
                  fontSize: { xs: '0.7rem', md: '0.875rem' }
                }}
              >
                WELCOME TO GLARE
              </Typography>
              
              <Typography 
                variant="h2" 
                component={motion.h2}
                sx={{ 
                  fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3.5rem' },
                  fontWeight: 700,
                  mb: { xs: 1.5, md: 2 },
                  lineHeight: 1.2,
                  background: 'linear-gradient(135deg, #4F46E5 0%, #b65f3b 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Don't Pay for Tutoring
              </Typography>

              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#666',
                  maxWidth: '500px',
                  mb: 4,
                  lineHeight: 1.6,
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.3rem' },
                }}
              >
                Transform your learning with AI-powered tutoring. Get instant analysis,
                expert explanations, and personalized guidance without the cost of traditional tutoring.
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                gap: { xs: 1, sm: 2 },
                mt: { xs: 3, md: 4 },
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                <Button
                  variant="outlined"
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#4F46E5',
                    borderColor: '#4F46E5',
                    py: 1.5,
                    px: 4,
                    borderRadius: '8px',
                    '&:hover': {
                      borderColor: '#3730A3',
                      bgcolor: 'rgba(79, 70, 229, 0.08)',
                    }
                  }}
                  onClick={scrollToLearnMore}
                >
                  Learn more ↓
                </Button>
              </Box>

              <Box 
                component={motion.div}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.8 }}
                sx={{ 
                  mt: 6, 
                  p: 3, 
                  borderRadius: '12px',
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(8px)',
                  border: '1px solid rgba(79, 70, 229, 0.1)',
                  maxWidth: 'fit-content'
                }}
              >
                <Typography variant="body2" sx={{ color: '#666', fontWeight: 500 }}>
                  Trusted by students worldwide
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 2 }}>
                  
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Right Side - Auth */}
        <Box sx={{ 
          flex: { xs: 'none', md: 1 },
          minHeight: { xs: '50vh', md: '100vh' },
          bgcolor: '#f5f5f5',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          p: { xs: 3, sm: 4, md: 6 },
        }}>
          <Box sx={{ 
            maxWidth: '400px', 
            mx: 'auto', 
            width: '100%',
            mt: { xs: 2, md: 0 }
          }}>
            {authError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {authError}
              </Alert>
            )}

            <Button
              fullWidth
              variant="contained"
              startIcon={<Google />}
              onClick={handleGoogleSignIn}
              sx={{
                mb: 2,
                py: 1.5,
                bgcolor: '#4285F4',
                color: '#fff',
                '&:hover': {
                  bgcolor: '#3574E2',
                }
              }}
            >
              Continue with Google
            </Button>

            <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/signup', { state: { email } })}
                    sx={{
                      mb: 2,
                      py: 1.5,
                      bgcolor: '#b65f3b',
                      color: '#fafafa',
                      '&:hover': {
                        bgcolor: '#8B4513',
                      }
                    }}
                  >
              Sign up with email
            </Button>

            <Box sx={{ position: 'relative', my: 3 }}>
              <Divider>
                <Typography sx={{ px: 2, color: '#666' }}>OR</Typography>
              </Divider>
            </Box>

            <Box component="form" onSubmit={(e) => e.preventDefault()}>
              <TextField
                fullWidth
                placeholder="Enter your email to sign in"
                variant="outlined"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!emailError}
                helperText={emailError}
                disabled={showPasswordInput}
                sx={{ mb: 2 }}
              />

              {showPasswordInput && !isNewUser && (
                <>
                  <TextField
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    variant="outlined"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePasswordVisibility}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                    Welcome back! Please enter your password
                  </Typography>
                </>
              )}

              {showPasswordInput && isNewUser && (
                <Box sx={{ mb: 3 }}>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    No account found with this email. Would you like to create one?
                  </Alert>
                  
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<Google />}
                    onClick={handleGoogleSignIn}
                    sx={{
                      mb: 2,
                      bgcolor: '#4285F4',
                      color: '#fff',
                      '&:hover': {
                        bgcolor: '#3574E2',
                      }
                    }}
                  >
                    Sign up with Google
                  </Button>

                  <Button
                    fullWidth
                    variant="contained"
                    onClick={() => navigate('/signup', { state: { email } })}
                    sx={{
                      bgcolor: '#b65f3b',
                      color: '#fff',
                      '&:hover': {
                        bgcolor: '#a35432',
                      }
                    }}
                  >
                    Sign up with email
                  </Button>

                  <Button
                    fullWidth
                    variant="text"
                    onClick={() => {
                      setShowPasswordInput(false);
                      setIsNewUser(false);
                      setEmail('');
                      setPassword('');
                      setEmailError('');
                      setAuthError('');
                    }}
                    sx={{
                      mt: 2,
                      color: '#4F46E5',
                      '&:hover': {
                        bgcolor: 'rgba(79, 70, 229, 0.04)',
                      }
                    }}
                  >
                    Try a different email
                  </Button>
                </Box>
              )}

              {!showPasswordInput && (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleEmailContinue}
                  sx={{
                    py: 1.5,
                    bgcolor: '#b65f3b',
                    '&:hover': {
                      bgcolor: '#a35432',
                    }
                  }}
                >
                  Continue
                </Button>
              )}

              {showPasswordInput && !isNewUser && (
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handlePasswordSubmit}
                  sx={{
                    py: 1.5,
                    bgcolor: '#b65f3b',
                    '&:hover': {
                      bgcolor: '#a35432',
                    }
                  }}
                >
                  Sign In
                </Button>
              )}
            </Box>

            <Typography variant="caption" sx={{ display: 'block', mt: 3, color: '#666', textAlign: 'center' }}>
              By continuing, you agree to our Terms of Service and Privacy Policy
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Learn More Section */}
      <Box 
        ref={learnMoreRef}
        sx={{ 
          width: '100%',
          bgcolor: '#fff',
          py: { xs: 6, md: 12 },
          px: { xs: 2, sm: 4, md: 6 },
        }}
      >
        <Container maxWidth="lg">
          <Box 
            component={motion.div}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            sx={{ textAlign: 'center', mb: 8 }}
          >
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 700,
                mb: 2,
                background: 'linear-gradient(135deg, #4F46E5 0%, #b65f3b 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Transform Your Learning Experience
            </Typography>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#666',
                maxWidth: '800px',
                mx: 'auto',
                mb: 6,
              }}
            >
              Powered by advanced AI technology to provide personalized tutoring across multiple subjects
            </Typography>
          </Box>

          {/* Features Grid */}
          <Box 
            sx={{ 
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' },
              gap: 4,
              mb: 8,
            }}
          >
            {/* AI Analysis Card */}
            <Paper
              component={motion.div}
              whileHover={{ y: -8 }}
              sx={{ 
                p: 4,
                borderRadius: 4,
                bgcolor: '#fff',
                boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                }
              }}
            >
              <AutoGraph sx={{ fontSize: 48, color: '#4F46E5', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                AI-Powered Analysis
              </Typography>
              <Typography color="text.secondary">
                Get instant, detailed feedback on your answers with our advanced AI analysis system.
                Understand your mistakes and learn from them effectively.
              </Typography>
            </Paper>

            {/* Wrong Answer Journal Card */}
            <Paper
              component={motion.div}
              whileHover={{ y: -8 }}
              sx={{ 
                p: 4,
                borderRadius: 4,
                bgcolor: '#fff',
                boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                }
              }}
            >
              <Assignment sx={{ fontSize: 48, color: '#b65f3b', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Wrong Answer Journal
              </Typography>
              <Typography color="text.secondary">
                Track your progress and learn from your mistakes with our intelligent wrong answer journal.
                Review patterns and improve systematically.
              </Typography>
            </Paper>

            {/* Multi-Subject Support Card */}
            <Paper
              component={motion.div}
              whileHover={{ y: -8 }}
              sx={{ 
                p: 4,
                borderRadius: 4,
                bgcolor: '#fff',
                boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                }
              }}
            >
              <School sx={{ fontSize: 48, color: '#4CAF50', mb: 2 }} />
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
                Multi-Subject Support
              </Typography>
              <Typography color="text.secondary">
                Get expert tutoring across multiple subjects
                with our versatile AI tutor.
              </Typography>
            </Paper>
          </Box>

          {/* Subject List */}
          <Box 
            component={motion.div}
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            sx={{ 
              textAlign: 'center',
              p: 4,
              borderRadius: 4,
              bgcolor: 'rgba(79, 70, 229, 0.04)',
              mb: 8,
            }}
          >
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;